<!--
  OTPInput.svelte — Six-digit one-time-password input.

  Renders a row of individual single-character inputs that auto-advance
  on keystroke.  Dispatches `complete` when all 6 digits are filled,
  with the concatenated code string.

  Used by Login (email OTP), TwoFactor, and VerifyAccount flows.

  @event complete — Fired with the 6-digit code string.
  @prop {string} [error] — Optional error message shown below the inputs.
-->
<script lang="ts">
	import { Helper } from 'flowbite-svelte';

	
	interface Props {
		/** Error message shown below the OTP inputs in red. */
		error?: string;
		/** Callback fired with the 6-digit code string when all digits are filled. */
		oncomplete?: (_code: string) => void;
	}

	let { error = '', oncomplete }: Props = $props();

	/** Number of OTP digits. */
	const LENGTH = 6;

	/** Array of individual digit values. */
	let digits: string[] = $state(Array(LENGTH).fill(''));

	/** References to each input element for focus management. */
	let inputs: HTMLInputElement[] = $state([]);

	/** Concatenated code from all digit inputs. */
	let code = $derived(digits.join(''));

	/**
	 * Handle input on a single digit field.
	 * Advances focus to the next input and dispatches `complete` when full.
	 */
	function handleInput(index: number, event: Event): void {
		const target = event.target as HTMLInputElement;
		/* Only allow single digits. */
		const value = target.value.replace(/\D/g, '').slice(-1);
		digits[index] = value;

		/** Auto-advance to the next input when a digit is entered. */
		if (value && index < LENGTH - 1) {
			/* Auto-advance to next input. */
			inputs[index + 1]?.focus();
		}

		/** Dispatch complete event when all digits are filled. */
		if (digits.every((d) => d.length === 1)) {
			oncomplete?.(code);
		}
	}

	/**
	 * Handle keydown for backspace navigation.
	 * Moves focus to the previous input on backspace when the current is empty.
	 */
	function handleKeydown(index: number, event: KeyboardEvent): void {
		if (event.key === 'Backspace' && !digits[index] && index > 0) {
			inputs[index - 1]?.focus();
		}
	}

	/**
	 * Handle paste — distribute pasted digits across all inputs.
	 */
	function handlePaste(event: ClipboardEvent): void {
		event.preventDefault();
		const pasted = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, LENGTH);
		/** Distribute pasted digits across all input fields. */
		for (let i = 0; i < LENGTH; i++) {
			digits[i] = pasted[i] || '';
		}
		/* Focus the last filled input (or first empty one). */
		const focusIndex = Math.min(pasted.length, LENGTH - 1);
		inputs[focusIndex]?.focus();

		/** Dispatch complete event if the paste filled all digits. */
		if (digits.every((d) => d.length === 1)) {
			oncomplete?.(digits.join(''));
		}
	}

	/** Reset all digits and focus the first input. */
	export function reset(): void {
		digits = Array(LENGTH).fill('');
		inputs[0]?.focus();
	}
</script>

<div class="mb-4">
	<div class="flex justify-center gap-2" onpaste={handlePaste}>
		{#each digits as digit, i (i)}
			<input
				bind:this={inputs[i]}
				type="text"
				inputmode="numeric"
				maxlength="1"
				value={digit}
				class="h-14 w-12 rounded-lg border text-center font-mono text-2xl
					{error
					? 'border-red-500 focus:border-red-500 focus:ring-red-500'
					: 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600'}
					bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white"
				oninput={(e) => handleInput(i, e)}
				onkeydown={(e) => handleKeydown(i, e)}
			/>
		{/each}
	</div>
	{#if error}
		<Helper class="mt-2 text-center" color="red">{error}</Helper>
	{/if}
</div>
